import { Component, EventEmitter, inject, Output } from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { HomeStore } from '@features/home/store/home.store';
import { HomePageTileType, HomeTile } from '@features/home/models/home.model';
import { NMFTAIconComponent } from '@shared/components/atoms/icon/icon.component';
import { ButtonComponent } from '@shared/components/atoms/button/button.component';
import { TranslateModule } from '@ngx-translate/core';

export interface Tile {
  name: string;
  icon: string;
}

@Component({
    selector: 'nmfta-side-panel',
    imports: [
        MatIconModule,
        CdkDropList,
        CdkDrag,
        TranslateModule,
        CdkDropListGroup,
        CdkDragHandle,
        CdkDragPlaceholder,
        NMFTAIconComponent,
        ButtonComponent,
    ],
    templateUrl: './side-panel.component.html',
    styleUrl: './side-panel.component.scss'
})
export class SidePanelComponent {
  store = inject(HomeStore);

  getIcon(type: HomePageTileType): string {
    const iconMap: Record<HomePageTileType, string> = {
      [HomePageTileType.None]: 'help',
      [HomePageTileType.SearchHistory]: 'schedule',
      [HomePageTileType.Favorite]: 'star',
      [HomePageTileType.Synonym]: 'synonym',
      [HomePageTileType.Comment]: 'forum',
      [HomePageTileType.NmfcBook]: 'description',
      [HomePageTileType.CompanyCommon]: 'groups',
      [HomePageTileType.Default]: 'help',
    };
    return iconMap[type] || 'help';
  }

  @Output() closePanel = new EventEmitter<void>();

  pinned = this.store.pinned;
  unpinned = this.store.unpinned;

  close() {
    this.closePanel.emit();
  }

  drop(event: CdkDragDrop<HomeTile[]>) {
    if (event.previousContainer === event.container) {
      var reorderedList = [...event.container.data];
      moveItemInArray(reorderedList, event.previousIndex, event.currentIndex);

      reorderedList = reorderedList.map((tile, index) => ({
        ...tile,
        position: index
      }));

      if (event.container.id === 'pinned') {
        this.store.updatePinnedOrder(reorderedList);
      } else if (event.container.id === 'unpinned') {
        this.store.updateUnpinnedOrder(reorderedList);
      }
      this.updateHomeTiles();
    } else {
      var previousList = [...event.previousContainer.data];
      var currentList = [...event.container.data];

      transferArrayItem(previousList, currentList, event.previousIndex, event.currentIndex);

      previousList = previousList.map((tile, index) => ({
        ...tile,
        position: index
      }));

      currentList = currentList.map((tile, index) => ({
        ...tile,
        position: index
      }));

      if (event.container.id === 'pinned') {
        this.store.updatePinnedOrder(currentList);
        this.store.updateUnpinnedOrder(previousList);
      } else if (event.container.id === 'unpinned') {
        this.store.updateUnpinnedOrder(currentList);
        this.store.updatePinnedOrder(previousList);
      }
      this.updateHomeTiles();
    }
  }

  unpinItem(index: number) {
    const item = this.pinned()[index];
    this.store.unpinItem(item);
    this.updateHomeTiles();
  }

  pinItem(index: number) {
    const item = this.unpinned()[index];
    this.store.pinItem(item);
    this.updateHomeTiles();
  }

  updateHomeTiles() {
    this.store.updateHomePageTiles();
  }

  handleKeydown(event: KeyboardEvent, index: number, action: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (action === 'pin') {
        this.pinItem(index);
      } else if (action === 'unpin') {
        this.unpinItem(index);
      }
    }
  }
}
